import { doc, setDoc, getDoc } from 'firebase/firestore'
import { firestore } from '@/firebase'
import createLogger from '@/utils/logger'
import { signupStoreDataSchema, type CustomerId, type SignupStoreData } from '@gh/common/models'

export { signupStoreDataSchema, type SignupStoreData }

const logger = createLogger('stores/signupPersistence.ts')

const COLLECTION_NAME = 'signup_sessions'
const LOCAL_STORAGE_KEY = 'signup-session'

export class SignupPersistence {
  private static getDocumentRef(customerId: CustomerId) {
    return doc(firestore, COLLECTION_NAME, customerId)
  }

  static async save(
    customerId: CustomerId | null,
    data: Omit<SignupStoreData, 'lastUpdated'>,
  ): Promise<void> {
    if (customerId) {
      await this.saveToFirestore(customerId, data)
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    } else {
      this.saveToLocalStorage(LOCAL_STORAGE_KEY, data)
    }
  }

  static async saveToFirestore(
    customerId: CustomerId,
    data: Omit<SignupStoreData, 'lastUpdated'>,
  ): Promise<void> {
    logger.log('saveToFirestore', customerId, data)
    try {
      const docRef = this.getDocumentRef(customerId)
      const dataToSave: SignupStoreData = {
        ...data,
        lastUpdated: new Date(),
      }

      await setDoc(docRef, dataToSave, { merge: true })
    } catch (error) {
      console.error('Error saving signup data to Firestore:', error)
      throw error
    }
  }

  static saveToLocalStorage(
    customerId: CustomerId,
    data: Omit<SignupStoreData, 'lastUpdated'>,
  ): void {
    try {
      localStorage.setItem(customerId, JSON.stringify(data))
    } catch (error) {
      console.error('Error saving signup data to LocalStorage:', error)
      throw error
    }
  }

  static async load(customerId: CustomerId | null): Promise<SignupStoreData | null> {
    if (customerId) {
      return this.loadFromFirestore(customerId)
    } else {
      return Promise.resolve(this.loadFromLocalStorage(LOCAL_STORAGE_KEY))
    }
  }

  static async loadFromFirestore(customerId: CustomerId): Promise<SignupStoreData | null> {
    try {
      const docRef = this.getDocumentRef(customerId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const parsed = signupStoreDataSchema.safeParse(docSnap.data())
        if (!parsed.success) {
          console.error('Error parsing signup data from Firestore:', parsed.error)
          return null
        }
        logger.log('loadFromFirestore', customerId, parsed.data)
        return parsed.data
      } else {
        return null
      }
    } catch (error) {
      console.error('Error loading signup data from Firestore:', error)
      throw error
    }
  }

  static loadFromLocalStorage(customerId: CustomerId): SignupStoreData | null {
    try {
      const data = localStorage.getItem(customerId)
      logger.log('loadFromLocalStorage', customerId, data)
      if (data) {
        const parsed = signupStoreDataSchema.safeParse(JSON.parse(data))
        if (!parsed.success) {
          console.error('Error parsing signup data from LocalStorage:', parsed.error)
          return null
        }
        return parsed.data
      } else {
        return null
      }
    } catch (error) {
      console.error('Error loading signup data from LocalStorage:', error)
      throw error
    }
  }
}
